import { useState, useEffect } from "react";
import axios from "axios";
import { Layers, Box, Server, Globe, ChevronRight, Search, Filter, Copy, CheckCheck } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCES = ["Pods", "Deployments", "Namespaces", "Nodes", "Services"];
const statusStyle = {
  Running: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  Ready: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  Active: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  Warning: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  CrashLoopBackOff: "text-rose-400 bg-rose-400/10 border-rose-400/20",
  Pending: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  NotReady: "text-rose-400 bg-rose-400/10 border-rose-400/20",
};

function YamlViewer({ resource, onClose }) {
  const [copied, setCopied] = useState(false);
  const yaml = `apiVersion: apps/v1
kind: ${resource.type?.replace(/s$/, '') || 'Pod'}
metadata:
  name: ${resource.name}
  namespace: ${resource.namespace || 'default'}
  labels:
    app: ${resource.name}
    version: "1.0"
spec:
  replicas: ${resource.replicas || 1}
  selector:
    matchLabels:
      app: ${resource.name}
  template:
    metadata:
      labels:
        app: ${resource.name}
    spec:
      containers:
      - name: ${resource.name}
        image: ${resource.image || 'nginx:latest'}
        resources:
          requests:
            cpu: ${resource.cpu_request || '100m'}
            memory: ${resource.memory_request || '128Mi'}
          limits:
            cpu: ${resource.cpu_limit || '500m'}
            memory: ${resource.memory_limit || '512Mi'}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(yaml);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(2, 6, 23, 0.8)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700 rounded-md overflow-hidden shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div>
            <p className="text-sm font-semibold text-slate-100 font-mono">{resource.name}</p>
            <p className="text-[10px] text-slate-500">{resource.namespace || "cluster-scoped"}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCopy} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-slate-700 text-xs text-slate-400 hover:text-slate-200 transition-colors">
              {copied ? <CheckCheck size={11} className="text-emerald-400" /> : <Copy size={11} />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-300 text-lg leading-none">×</button>
          </div>
        </div>
        <pre className="p-4 text-xs text-slate-300 overflow-auto font-mono max-h-96 leading-relaxed bg-slate-950/50">{yaml}</pre>
      </div>
    </div>
  );
}

export default function ResourceExplorer() {
  const { clusterId } = useCluster();
  const [activeResource, setActiveResource] = useState("Pods");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [namespace, setNamespace] = useState("all");
  const [namespaces, setNamespaces] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);

  useEffect(() => {
    axios.get(`${API}/clusters/${clusterId}/namespaces`)
      .then((r) => setNamespaces(r.data.map((n) => n.name)));
  }, [clusterId]);

  useEffect(() => {
    setLoading(true);
    setSearch("");
    const endpoints = {
      Pods: `/clusters/${clusterId}/pods${namespace !== "all" ? `?namespace=${namespace}` : ""}`,
      Deployments: `/clusters/${clusterId}/deployments${namespace !== "all" ? `?namespace=${namespace}` : ""}`,
      Namespaces: `/clusters/${clusterId}/namespaces`,
      Nodes: `/clusters/${clusterId}/nodes`,
      Services: `/clusters/${clusterId}/pods`,
    };
    axios.get(`${API}${endpoints[activeResource]}`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clusterId, activeResource, namespace]);

  const filtered = data.filter((item) => {
    const name = item.name || "";
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const cols = {
    Pods: ["Name", "Namespace", "Status", "Image", "Restarts", "Node", "Age"],
    Deployments: ["Name", "Namespace", "Replicas", "Status", "Image", "Health", "Age"],
    Namespaces: ["Name", "Status", "Pods", "CPU Usage", "Memory", "Age"],
    Nodes: ["Name", "Status", "Role", "CPU %", "Memory %", "Pods", "Age"],
    Services: ["Name", "Namespace", "Status", "Image", "Age"],
  };

  const renderRow = (item, i) => {
    const s = item.status || "Running";
    const statusEl = <span className={cn("text-[10px] px-1.5 py-0.5 rounded border", statusStyle[s] || statusStyle.Warning)}>{s}</span>;

    if (activeResource === "Pods") return (
      <tr key={i} className="border-b border-slate-800/40 hover:bg-slate-800/20 cursor-pointer transition-colors" onClick={() => setSelectedResource({ ...item, type: "Pod" })}>
        <td className="px-4 py-2.5 text-slate-200 font-mono text-xs truncate max-w-[180px]">{item.name}</td>
        <td className="px-4 py-2.5 text-slate-400 text-xs">{item.namespace}</td>
        <td className="px-4 py-2.5">{statusEl}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs truncate max-w-[140px]">{item.image}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{item.restarts}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{item.node}</td>
        <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{item.age}</td>
      </tr>
    );
    if (activeResource === "Deployments") return (
      <tr key={i} className="border-b border-slate-800/40 hover:bg-slate-800/20 cursor-pointer transition-colors" onClick={() => setSelectedResource({ ...item, type: "Deployment" })}>
        <td className="px-4 py-2.5 text-slate-200 font-mono text-xs">{item.name}</td>
        <td className="px-4 py-2.5 text-slate-400 text-xs">{item.namespace}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{item.ready_replicas}/{item.replicas}</td>
        <td className="px-4 py-2.5">{statusEl}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs truncate max-w-[140px]">{item.image}</td>
        <td className="px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <div className="w-14 h-1 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${item.health_score}%` }} />
            </div>
            <span className="font-mono text-xs text-slate-400">{item.health_score}</span>
          </div>
        </td>
        <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{item.age}</td>
      </tr>
    );
    if (activeResource === "Namespaces") return (
      <tr key={i} className="border-b border-slate-800/40 hover:bg-slate-800/20 cursor-pointer transition-colors" onClick={() => setSelectedResource({ ...item, type: "Namespace" })}>
        <td className="px-4 py-2.5 text-slate-200 font-mono text-xs">{item.name}</td>
        <td className="px-4 py-2.5">{statusEl}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{item.pods}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{item.cpu_usage?.toFixed(1)} cores</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{item.memory_gb?.toFixed(1)} GB</td>
        <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{item.age}</td>
      </tr>
    );
    if (activeResource === "Nodes") return (
      <tr key={i} className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors" key={i}>
        <td className="px-4 py-2.5 text-slate-200 font-mono text-xs">{item.name}</td>
        <td className="px-4 py-2.5">{statusEl}</td>
        <td className="px-4 py-2.5 text-slate-400 text-xs">{item.role}</td>
        <td className="px-4 py-2.5 font-mono text-xs" style={{ color: item.cpu_percent > 80 ? "#f87171" : "#34d399" }}>{item.cpu_percent?.toFixed(0)}%</td>
        <td className="px-4 py-2.5 font-mono text-xs" style={{ color: item.memory_percent > 85 ? "#f87171" : "#34d399" }}>{item.memory_percent?.toFixed(0)}%</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{item.pods_count}</td>
        <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{item.age}</td>
      </tr>
    );
    return (
      <tr key={i} className="border-b border-slate-800/40 hover:bg-slate-800/20 cursor-pointer transition-colors" onClick={() => setSelectedResource(item)}>
        <td className="px-4 py-2.5 text-slate-200 font-mono text-xs">{item.name}</td>
        <td className="px-4 py-2.5 text-slate-400 text-xs">{item.namespace}</td>
        <td className="px-4 py-2.5">{statusEl}</td>
        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs truncate max-w-[140px]">{item.image}</td>
        <td className="px-4 py-2.5 text-slate-500 text-xs font-mono">{item.age}</td>
      </tr>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Resource Explorer</h2>
        <span className="text-xs text-slate-500 font-mono">{filtered.length} resources</span>
      </div>

      {/* Resource tabs */}
      <div className="flex items-center gap-1 bg-slate-900/50 border border-slate-800 rounded-md p-1 overflow-x-auto">
        {RESOURCES.map((r) => (
          <button key={r} onClick={() => setActiveResource(r)}
            className={cn("px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap transition-colors",
              activeResource === r ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "text-slate-400 hover:text-slate-200")}>
            {r}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-slate-900/50 border border-slate-800 rounded-md px-3 py-2 flex-1 max-w-xs">
          <Search size={12} className="text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={`Filter ${activeResource.toLowerCase()}...`}
            className="bg-transparent text-sm text-slate-200 placeholder-slate-500 outline-none flex-1 text-xs" />
        </div>
        {activeResource !== "Nodes" && activeResource !== "Namespaces" && (
          <select value={namespace} onChange={(e) => setNamespace(e.target.value)}
            className="bg-slate-900/50 border border-slate-800 rounded-md px-3 py-2 text-xs text-slate-300 outline-none">
            <option value="all">All namespaces</option>
            {namespaces.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
      </div>

      {/* Table */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-800">
                {cols[activeResource]?.map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}><td colSpan={7} className="px-4 py-3"><div className="h-3 bg-slate-800/80 rounded animate-pulse" /></td></tr>
                ))
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-sm">No resources found</td></tr>
              ) : (
                filtered.map((item, i) => renderRow(item, i))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedResource && <YamlViewer resource={selectedResource} onClose={() => setSelectedResource(null)} />}
    </div>
  );
}
